<template>
    <div class="container py-4" v-cloak>
        <!-- ヘッダー -->
        <div class="mb-4">
            <h2 class="fw-bold text-primary mb-1">
                <i class="fas fa-receipt me-2"></i>領収書アップロード
            </h2>
            <p class="text-muted mb-0">画像を選択して領収書情報を登録できます</p>
        </div>

        <!-- ファイル選択エリア -->
        <div class="card shadow-sm mb-4">
            <div class="card-body">
                <uploader
                    url="/api/tools/receipt-images"
                    :multiple="true"
                    :max-filesize="$store.state.max_filesize"
                    field-name="images[]"
                    @uploaded="reload()"
                />
            </div>
        </div>

        <!-- アップロード済み画像一覧 -->
        <div v-if="images.length" class="card shadow-sm">
            <div class="card-header bg-white py-3 uploaded-header">
                <div class="d-flex justify-content-between align-items-center">
                    <h5 class="mb-0 fw-bold">
                        <i class="fas fa-images me-2 text-success"></i>アップロード済み領収書
                        <span class="badge bg-success ms-2">{{ images.length }}</span>
                        <span v-if="receipt_image_ids.length" class="badge bg-primary ms-2">
                            {{ receipt_image_ids.length }}件選択中
                        </span>
                    </h5>
                    <div v-if="receipt_image_ids.length" class="btn-group">
                        <button class="btn btn-sm btn-primary" @click="editSelection">
                            <i class="fa-solid fa-pen-to-square"></i>
                            選択項目を編集
                        </button>
                        <button class="btn btn-sm btn-secondary" @click="clearSelection">
                            <i class="fa-solid fa-times me-1"></i>
                            選択解除
                        </button>
                        <button class="btn btn-sm btn-danger" @click="deleteSelectedImages">
                            <i class="fa-solid fa-xmark me-1"></i>
                            選択項目を抹消
                        </button>
                    </div>
                </div>
            </div>
            <div class="card-body">
                <div class="row g-3">
                    <div
                        class="col-12 col-sm-6 col-md-4 col-lg-3"
                        v-for="img in images"
                        :key="img.path"
                    >
                        <div class="card h-100 shadow-sm hover-lift" :class="{ 'border-primary border-2': isSelected(img.id) }">
                            <div class="position-relative overflow-hidden" style="height: 200px;">
                                <!-- チェックボックス -->
                                 <div class="position-absolute top-0 start-0 p-2" style="z-index: 10;">
                                    <label class="checkbox-container" @click.stop>
                                        <input
                                            type="checkbox"
                                            :checked="isSelected(img.id)"
                                            @change="toggleSelection(img.id)"
                                        />
                                        <span class="checkmark"></span>
                                    </label>
                                </div>
                                <div class="position-relative overflow-hidden" style="height: 200px;">
                                    <!-- チェックボックス -->
                                    <div class="position-absolute top-0 start-0 p-2" style="z-index: 10;">
                                        <label class="checkbox-container" @click.stop>
                                            <input
                                                type="checkbox"
                                                :checked="isSelected(img.id)"
                                                @change="toggleSelection(img.id)"
                                            />
                                            <span class="checkmark"></span>
                                        </label>
                                    </div>

                                    <!-- PDFバッジ -->
                                    <div
                                        v-if="isPdf(img)"
                                        class="position-absolute top-0 end-0 m-2 badge bg-danger"
                                        style="z-index: 10;"
                                    >
                                        <i class="fa-solid fa-file-pdf me-1"></i>PDF
                                    </div>

                                    <!-- 画像 -->
                                    <img
                                        v-if="!isPdf(img)"
                                        :src="img.path"
                                        class="w-100 h-100 object-fit-cover pointer"
                                        @click="openForm(img.id)"
                                        style="transition: transform 0.3s ease;"
                                    />

                                    <!-- PDFサムネイル -->
                                    <embed
                                        v-else
                                        :src="`${img.path}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`"
                                        type="application/pdf"
                                        class="w-100 h-100 pointer bg-light"
                                        @click="openForm(img.id)"
                                    />

                                    <div
                                        class="position-absolute pointer top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center opacity-0 hover-overlay"
                                        @click="openForm(img.id)"
                                    >
                                        <i class="fas fa-edit fa-2x text-white"></i>
                                    </div>
                                </div>

                                <div
                                    class="position-absolute pointer top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 d-flex align-items-center justify-content-center opacity-0 hover-overlay"
                                    @click="openForm(img.id)"
                                >
                                    <i class="fas fa-edit fa-2x text-white"></i>
                                </div>
                            </div>
                            <div class="card-body p-2">
                                <p class="small text-truncate mb-2" :title="img.file_name">
                                    <i class="fas fa-file me-1 text-muted"></i>{{ img.file_name }}
                                </p>
                                <button
                                    class="btn btn-danger btn-sm w-100"
                                    @click="deleteImage(img)">
                                    <i class="fa-solid fa-xmark"></i>
                                    抹消
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 画像未登録時のメッセージ -->
        <div v-else-if="!isLoading" class="text-center py-5">
            <i class="fas fa-inbox fa-4x text-muted mb-3"></i>
            <p class="text-muted">まだ画像がアップロードされていません</p>
        </div>

        <receipt-editor
            :receipt_image_id="receipt_image_id"
            @saved="reload"
            @close="closeModal"
        ></receipt-editor>
        <bulk-editor
            :receipt_image_ids="receipt_image_ids"
            @saved="reload"
            @close="closeBulk"
        ></bulk-editor>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
    </div>
</template>

<style scoped>
    .upload-zone {
        transition: all 0.3s ease;
    }

    .upload-zone:hover {
        background-color: rgba(var(--bs-primary-rgb), 0.05) !important;
    }

    .object-fit-cover {
        object-fit: cover;
    }

    .checkbox-large {
        width: 1.5rem;
        height: 1.5rem;
        cursor: pointer;
        background-color: white;
        border: 2px solid #dee2e6;
    }

    .checkbox-large:checked {
        background-color: #0d6efd;
        border-color: #0d6efd;
    }
    .uploaded-header {
        min-height: 64px;
    }

    /* カスタムチェックボックス */
    .checkbox-container {
        display: block;
        position: relative;
        cursor: pointer;
        user-select: none;
        width: 32px;
        height: 32px;
    }

    .checkbox-container input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
    }

    .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 32px;
        width: 32px;
        background-color: rgba(255, 255, 255, 0.95);
        border: 3px solid #6c757d;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        transition: all 0.2s ease;
    }

    .checkbox-container:hover .checkmark {
        background-color: rgba(255, 255, 255, 1);
        border-color: #0d6efd;
        transform: scale(1.1);
    }

    .checkbox-container input:checked ~ .checkmark {
        background-color: #0d6efd;
        border-color: #0d6efd;
    }

    .checkmark:after {
        content: "";
        position: absolute;
        display: none;
    }

    .checkbox-container input:checked ~ .checkmark:after {
        display: block;
    }

    .checkbox-container .checkmark:after {
        left: 10px;
        top: 5px;
        width: 8px;
        height: 14px;
        border: solid white;
        border-width: 0 3px 3px 0;
        transform: rotate(45deg);
    }
</style>

<script>
import receiptEditor from './receiptEditor.vue';
import bulkEditor from './bulkEditor.vue';
import uploader from 'components/uploader.vue';
export default {
    components: {
        receiptEditor,
        bulkEditor,
        uploader
    },
    data() {
        return {
            isLoading: false,
            modals: [],
            images: [],
            receipt_image_id: null,
            receipt_image_ids: [],
        }
    },
    methods: {
        closeModal(id) {
            this.modals[id]?.hide();
        },
        reload() {
            this.isLoading = true;
            axios.get('/api/tools/receipt-images').then(res => {
                this.images = res.data;
                this.isLoading = false;
                // リロード後、存在しない画像の選択を解除
                this.receipt_image_ids = this.receipt_image_ids.filter(id =>
                    this.images.some(img => img.id === id)
                );
            }).catch(err => {
                Alert.err(err);
                this.isLoading = false;
            });
        },
        deleteImage(img) {
            swal({
                title: "画像抹消",
                text: "この画像を抹消します。\nよろしいですか？",
                icon: "error",
                buttons: ["キャンセル", "抹消"],
                dangerMode: true,
            }).then(confirm => {
                if (confirm) {
                    axios.post('/api/tools/receipt-images/delete', {
                        path: img.path,
                        id: img.id,
                    }).then(res => {
                        this.reload();
                    }).catch(err => {
                        Alert.err(err);
                    });
                }
            });
        },
        deleteSelectedImages() {
            const count = this.receipt_image_ids.length;
            swal({
                title: "選択画像の一括抹消",
                text: `選択中の${count}件の画像を抹消します。\nよろしいですか？`,
                icon: "error",
                buttons: ["キャンセル", "抹消"],
                dangerMode: true,
            }).then(confirm => {
                if (confirm) {
                    const images = this.images.filter(img =>
                        this.receipt_image_ids.includes(img.id)
                    );
                    this.isLoading = true;
                    axios.post('/api/tools/receipt-images/delete', {
                        images: images,
                    }).then(() => {
                        this.receipt_image_ids = [];
                        this.reload();
                    }).catch(err => {
                        Alert.err(err);
                        this.isLoading = false;
                    });
                }
            });
        },
        toggleSelection(imageId) {
            const index = this.receipt_image_ids.indexOf(imageId);
            if (index > -1) {
                this.receipt_image_ids.splice(index, 1);
            } else {
                this.receipt_image_ids.push(imageId);
            }
        },
        isSelected(imageId) {
            return this.receipt_image_ids.includes(imageId);
        },
        clearSelection() {
            this.receipt_image_ids = [];
        },
        editSelection() {
            this.modals['bulk-editor'].show();
        },
        closeBulk() {
            this.modals['bulk-editor'].hide();
        },
        openForm(id) {
            this.receipt_image_id = id;
            setTimeout(() => {
                this.modals['receipt-editor'].show();
            });
        },
        isPdf(file_item) {
            const file_name = `${file_item.file_name || ''}`.toLowerCase();
            const file_path = `${file_item.path || ''}`.toLowerCase();
            return file_name.endsWith('.pdf') || file_path.endsWith('.pdf');
        },

    },
    mounted() {
        this.modals['receipt-editor'] = new bootstrap.Modal(document.getElementById('receipt-editor'));
        this.modals['bulk-editor'] = new bootstrap.Modal(document.getElementById('bulk-editor'));
        this.reload();
    },
}
</script>
